import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import {
  Bot,
  Plus,
  RefreshCw,
  History,
  Shield,
  BookMarked,
  ListChecks,
  Archive,
  Ban,
  Workflow,
  Play,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { MCP_TIER1_POLICY } from '../../data/mcp-tier1-policy';
import DirectiveModal from './DirectiveModal';
import BriefModal from './BriefModal';
import WorkflowModal from './WorkflowModal';
import type {
  AgentDirective,
  ContextBrief,
  DirectiveRevision,
  AgentWorkflow,
  WorkflowRevision,
} from './types';
import { SCOPE_COLORS, WORKFLOW_STATUS_BADGE } from './types';

type Tab = 'overview' | 'directives' | 'briefs' | 'workflows' | 'history';

const STATUS_BADGE: Record<string, string> = {
  active: 'bg-emerald-100 dark:bg-emerald-900 text-emerald-700 dark:text-emerald-300',
  superseded: 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400',
  revoked: 'bg-red-100 dark:bg-red-900 text-red-600 dark:text-red-300',
  archived: 'bg-gray-100 dark:bg-gray-700 text-gray-500',
};

function formatWhen(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

const AgentDirectivesView: React.FC = () => {
  const { user } = useAuth();
  const [tab, setTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);
  const [directives, setDirectives] = useState<AgentDirective[]>([]);
  const [briefs, setBriefs] = useState<ContextBrief[]>([]);
  const [workflows, setWorkflows] = useState<AgentWorkflow[]>([]);
  const [revisions, setRevisions] = useState<DirectiveRevision[]>([]);
  const [workflowRevisions, setWorkflowRevisions] = useState<WorkflowRevision[]>([]);
  const [showDirectiveModal, setShowDirectiveModal] = useState(false);
  const [showBriefModal, setShowBriefModal] = useState(false);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [editingDirective, setEditingDirective] = useState<AgentDirective | null>(null);
  const [editingBrief, setEditingBrief] = useState<ContextBrief | null>(null);
  const [editingWorkflow, setEditingWorkflow] = useState<AgentWorkflow | null>(null);
  const [statusFilter, setStatusFilter] = useState<'active' | 'all'>('active');
  const [workflowStatusFilter, setWorkflowStatusFilter] = useState<
    'active' | 'draft' | 'all'
  >('active');

  const fetchAll = useCallback(async () => {
    try {
      setLoading(true);
      const [dirRes, briefRes, wfRes, revRes, wfRevRes] = await Promise.all([
        supabase
          .from('agent_directives')
          .select('*')
          .order('priority', { ascending: true })
          .order('updated_at', { ascending: false }),
        supabase
          .from('agent_context_briefs')
          .select('*')
          .order('priority', { ascending: true })
          .order('updated_at', { ascending: false }),
        supabase
          .from('agent_workflows')
          .select('*')
          .order('priority', { ascending: true })
          .order('updated_at', { ascending: false }),
        supabase
          .from('agent_directive_revisions')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(100),
        supabase
          .from('agent_workflow_revisions')
          .select('*')
          .order('created_at', { ascending: false })
          .limit(100),
      ]);

      if (dirRes.error) throw dirRes.error;
      if (briefRes.error) throw briefRes.error;
      if (wfRes.error) throw wfRes.error;
      if (revRes.error) throw revRes.error;
      if (wfRevRes.error) throw wfRevRes.error;

      setDirectives((dirRes.data as AgentDirective[]) ?? []);
      setBriefs((briefRes.data as ContextBrief[]) ?? []);
      setWorkflows((wfRes.data as AgentWorkflow[]) ?? []);
      setRevisions((revRes.data as DirectiveRevision[]) ?? []);
      setWorkflowRevisions((wfRevRes.data as WorkflowRevision[]) ?? []);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`Failed to load: ${msg}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
  }, [user, fetchAll]);

  const activeDirectives = directives.filter((d) => d.status === 'active');
  const activeBriefs = briefs.filter((b) => b.status === 'active');
  const activeWorkflows = workflows.filter((w) => w.status === 'active');
  const filteredDirectives =
    statusFilter === 'active' ? activeDirectives : directives;
  const filteredWorkflows = workflows.filter((w) => {
    if (workflowStatusFilter === 'all') return true;
    return w.status === workflowStatusFilter;
  });

  const handleRevoke = async (d: AgentDirective) => {
    if (!window.confirm(`Revoke directive "${d.key}"?`)) return;
    try {
      const { error } = await supabase
        .from('agent_directives')
        .update({ status: 'revoked', updated_at: new Date().toISOString() })
        .eq('id', d.id);
      if (error) throw error;
      toast.success('Directive revoked');
      fetchAll();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const handleArchiveBrief = async (b: ContextBrief) => {
    if (!window.confirm(`Archive brief "${b.slug}"?`)) return;
    try {
      const { error } = await supabase
        .from('agent_context_briefs')
        .update({ status: 'archived', updated_at: new Date().toISOString() })
        .eq('id', b.id);
      if (error) throw error;
      toast.success('Brief archived');
      fetchAll();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const handleActivateWorkflow = async (w: AgentWorkflow) => {
    if (!window.confirm(`Activate workflow "${w.slug}"?`)) return;
    try {
      const existingActive = workflows.find(
        (row) => row.slug === w.slug && row.status === 'active',
      );
      const now = new Date().toISOString();

      if (existingActive) {
        const { data: inserted, error: insertError } = await supabase
          .from('agent_workflows')
          .insert({
            slug: w.slug,
            title: w.title,
            triggers: w.triggers,
            steps: w.steps,
            success_shape: w.success_shape,
            anti_patterns: w.anti_patterns,
            status: 'active',
            priority: w.priority,
            version: (existingActive.version ?? 1) + 1,
            created_via: 'engine_ui',
            updated_at: now,
          })
          .select('id')
          .single();
        if (insertError) throw insertError;

        const { error: supersedeError } = await supabase
          .from('agent_workflows')
          .update({
            status: 'superseded',
            superseded_by: inserted?.id ?? null,
            updated_at: now,
          })
          .eq('id', existingActive.id);
        if (supersedeError) throw supersedeError;
      } else {
        const { error } = await supabase
          .from('agent_workflows')
          .update({ status: 'active', updated_at: now })
          .eq('id', w.id);
        if (error) throw error;
      }

      toast.success('Workflow activated');
      fetchAll();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const handleRetireWorkflow = async (w: AgentWorkflow) => {
    if (!window.confirm(`Retire active workflow "${w.slug}"?`)) return;
    try {
      const { error } = await supabase
        .from('agent_workflows')
        .update({ status: 'retired', updated_at: new Date().toISOString() })
        .eq('id', w.id);
      if (error) throw error;
      toast.success('Workflow retired');
      fetchAll();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : String(err));
    }
  };

  const directiveById = new Map(directives.map((d) => [d.id, d]));
  const workflowById = new Map(workflows.map((w) => [w.id, w]));

  const tabs: { id: Tab; label: string; icon: React.ReactNode }[] = [
    { id: 'overview', label: 'Overview', icon: <Bot className="w-4 h-4" /> },
    { id: 'directives', label: 'Tier 2 — Directives', icon: <ListChecks className="w-4 h-4" /> },
    { id: 'briefs', label: 'Tier 3 — Briefs', icon: <BookMarked className="w-4 h-4" /> },
    { id: 'workflows', label: 'Tier 4 — Workflows', icon: <Workflow className="w-4 h-4" /> },
    { id: 'history', label: 'History', icon: <History className="w-4 h-4" /> },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Bot className="w-7 h-7 text-violet-500" />
            MCP configuration & memory
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Hosted connector memory — Tier 1 compiled policy, Tier 2 directives, Tier 3 briefs, Tier 4
            workflows. Loaded by <code className="text-xs">engine_get_session_context</code> at{' '}
            <a
              href="https://mcp.stephansmith.me/mcp"
              className="text-violet-600 hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              mcp.stephansmith.me
            </a>
          </p>
        </div>
        <button
          type="button"
          onClick={fetchAll}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-6 border-b border-gray-200 dark:border-gray-700 pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-t-md border-b-2 -mb-[2px] ${
              tab === t.id
                ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400 font-medium'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-1">
                <Shield className="w-4 h-4 text-sky-500" />
                Tier 1 — Compiled
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">Deploy-time</p>
              <p className="text-xs text-gray-500 mt-1">engine-mcp/src/policy.ts</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-1">
                <ListChecks className="w-4 h-4 text-violet-500" />
                Tier 2 — Active directives
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{activeDirectives.length}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-1">
                <BookMarked className="w-4 h-4 text-amber-500" />
                Tier 3 — Active briefs
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{activeBriefs.length}</p>
            </div>
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-500 mb-1">
                <Workflow className="w-4 h-4 text-teal-500" />
                Tier 4 — Active workflows
              </div>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">{activeWorkflows.length}</p>
              <p className="text-xs text-gray-500 mt-1">Tool-chain playbooks</p>
            </div>
          </div>

          <p className="text-xs text-gray-500 dark:text-gray-400">
            Precedence: Tier 1 policy → Tier 2 directives → Tier 4 workflows → improvisation.
            Workflows never override safety or confirm rules.
          </p>

          <section className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-5">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2 mb-3">
              <Shield className="w-5 h-5 text-sky-500" />
              Tier 1 — Compiled policy (read-only)
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              {MCP_TIER1_POLICY.version_note} Repo: {MCP_TIER1_POLICY.repo} ·{' '}
              <a href={MCP_TIER1_POLICY.hosted_url} className="text-emerald-600 hover:underline" target="_blank" rel="noreferrer">
                {MCP_TIER1_POLICY.hosted_url}
              </a>
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1 mb-4">
              {MCP_TIER1_POLICY.memory_layers.map((line) => (
                <li key={line}>• {line}</li>
              ))}
            </ul>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h3 className="text-xs font-semibold uppercase text-emerald-600 mb-2">Allowed</h3>
                <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                  {MCP_TIER1_POLICY.allowed.map((a) => (
                    <li key={a}>✓ {a}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h3 className="text-xs font-semibold uppercase text-red-600 mb-2">Forbidden</h3>
                <ul className="text-sm space-y-1 text-gray-600 dark:text-gray-300">
                  {MCP_TIER1_POLICY.forbidden.map((f) => (
                    <li key={f}>✗ {f}</li>
                  ))}
                </ul>
              </div>
            </div>
            <p className="text-xs text-gray-500 mt-4 italic">{MCP_TIER1_POLICY.locked_send_rule}</p>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Current Tier 2 snapshot
            </h2>
            <div className="space-y-2">
              {activeDirectives.slice(0, 5).map((d) => (
                <div
                  key={d.id}
                  className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 px-4 py-3"
                >
                  <span className="font-mono text-xs text-gray-500">{d.key}</span>
                  <p className="text-sm text-gray-800 dark:text-gray-200 mt-1 line-clamp-2">{d.directive}</p>
                </div>
              ))}
              {activeDirectives.length === 0 && (
                <p className="text-sm text-gray-500">No active directives yet.</p>
              )}
            </div>
          </section>
        </div>
      )}

      {tab === 'directives' && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'active' | 'all')}
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700"
            >
              <option value="active">Active only</option>
              <option value="all">All statuses</option>
            </select>
            <button
              type="button"
              onClick={() => {
                setEditingDirective(null);
                setShowDirectiveModal(true);
              }}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4" />
              New directive
            </button>
          </div>

          <div className="space-y-3">
            {filteredDirectives.map((d) => (
              <div
                key={d.id}
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm font-medium text-gray-900 dark:text-white">
                      {d.key}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${SCOPE_COLORS[d.scope]}`}>
                      {d.scope}
                    </span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[d.status]}`}>
                      {d.status}
                    </span>
                    <span className="text-xs text-gray-400">p{d.priority}</span>
                  </div>
                  <div className="flex gap-2">
                    {d.status === 'active' && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingDirective(d);
                            setShowDirectiveModal(true);
                          }}
                          className="text-xs text-emerald-600 hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRevoke(d)}
                          className="text-xs text-red-600 hover:underline inline-flex items-center gap-1"
                        >
                          <Ban className="w-3 h-3" />
                          Revoke
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-800 dark:text-gray-200">{d.directive}</p>
                {d.rationale && (
                  <p className="text-xs text-gray-500 mt-2">Why: {d.rationale}</p>
                )}
                {d.applies_to?.length > 0 && (
                  <p className="text-xs text-gray-400 mt-2 font-mono">
                    Tools: {d.applies_to.join(', ')}
                  </p>
                )}
                <p className="text-xs text-gray-400 mt-2">
                  Updated {formatWhen(d.updated_at)} via {d.created_via}
                </p>
              </div>
            ))}
            {filteredDirectives.length === 0 && !loading && (
              <p className="text-sm text-gray-500 text-center py-8">No directives match this filter.</p>
            )}
          </div>
        </div>
      )}

      {tab === 'briefs' && (
        <div>
          <div className="flex justify-end mb-4">
            <button
              type="button"
              onClick={() => {
                setEditingBrief(null);
                setShowBriefModal(true);
              }}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4" />
              New brief
            </button>
          </div>
          <div className="space-y-3">
            {briefs.map((b) => (
              <div
                key={b.id}
                className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                  <div>
                    <span className="font-mono text-xs text-gray-500">{b.slug}</span>
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">{b.title}</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_BADGE[b.status]}`}>
                      {b.status}
                    </span>
                    {b.status === 'active' && (
                      <>
                        <button
                          type="button"
                          onClick={() => {
                            setEditingBrief(b);
                            setShowBriefModal(true);
                          }}
                          className="text-xs text-emerald-600 hover:underline"
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleArchiveBrief(b)}
                          className="text-xs text-gray-500 hover:underline inline-flex items-center gap-1"
                        >
                          <Archive className="w-3 h-3" />
                          Archive
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">{b.body}</p>
                <p className="text-xs text-gray-400 mt-2">
                  Updated {formatWhen(b.updated_at)}
                  {b.expires_at ? ` · expires ${formatWhen(b.expires_at)}` : ''}
                </p>
              </div>
            ))}
            {briefs.length === 0 && !loading && (
              <p className="text-sm text-gray-500 text-center py-8">No context briefs yet.</p>
            )}
          </div>
        </div>
      )}

      {tab === 'workflows' && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <select
              value={workflowStatusFilter}
              onChange={(e) =>
                setWorkflowStatusFilter(e.target.value as 'active' | 'draft' | 'all')
              }
              className="text-sm border border-gray-300 dark:border-gray-600 rounded-md px-3 py-2 bg-white dark:bg-gray-700"
            >
              <option value="active">Active only</option>
              <option value="draft">Drafts only</option>
              <option value="all">All statuses</option>
            </select>
            <button
              type="button"
              onClick={() => {
                setEditingWorkflow(null);
                setShowWorkflowModal(true);
              }}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
            >
              <Plus className="w-4 h-4" />
              New workflow
            </button>
          </div>

          <div className="space-y-3">
            {filteredWorkflows.map((w) => {
              const replacement = w.superseded_by ? workflowById.get(w.superseded_by) : null;
              return (
                <div
                  key={w.id}
                  className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
                >
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-mono text-sm font-medium text-gray-900 dark:text-white">
                        {w.slug}
                      </span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${WORKFLOW_STATUS_BADGE[w.status]}`}>
                        {w.status}
                      </span>
                      <span className="text-xs text-gray-400">v{w.version} · p{w.priority}</span>
                    </div>
                    <div className="flex gap-2">
                      {(w.status === 'active' || w.status === 'draft') && (
                        <button
                          type="button"
                          onClick={() => {
                            setEditingWorkflow(w);
                            setShowWorkflowModal(true);
                          }}
                          className="text-xs text-emerald-600 hover:underline"
                        >
                          Edit
                        </button>
                      )}
                      {w.status === 'draft' && (
                        <button
                          type="button"
                          onClick={() => handleActivateWorkflow(w)}
                          className="text-xs text-teal-600 hover:underline inline-flex items-center gap-1"
                        >
                          <Play className="w-3 h-3" />
                          Activate
                        </button>
                      )}
                      {w.status === 'active' && (
                        <button
                          type="button"
                          onClick={() => handleRetireWorkflow(w)}
                          className="text-xs text-red-600 hover:underline inline-flex items-center gap-1"
                        >
                          <Ban className="w-3 h-3" />
                          Retire
                        </button>
                      )}
                    </div>
                  </div>
                  <h3 className="text-base font-semibold text-gray-900 dark:text-white">{w.title}</h3>
                  {w.triggers?.length > 0 && (
                    <p className="text-xs text-gray-500 mt-2">
                      Triggers: {w.triggers.join(' · ')}
                    </p>
                  )}
                  <ol className="mt-3 space-y-1 text-sm text-gray-700 dark:text-gray-300 list-decimal list-inside">
                    {(w.steps ?? []).map((step, idx) => (
                      <li key={`${w.id}-step-${idx}`}>
                        <span className="font-mono text-xs">{step.tool}</span>
                        {step.purpose ? ` — ${step.purpose}` : ''}
                        {step.confirm_before ? ' (confirm)' : ''}
                      </li>
                    ))}
                  </ol>
                  {w.success_shape && (
                    <p className="text-xs text-gray-500 mt-2">Success: {w.success_shape}</p>
                  )}
                  {w.anti_patterns?.length > 0 && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                      Avoid: {w.anti_patterns.join(', ')}
                    </p>
                  )}
                  {w.status === 'superseded' && replacement && (
                    <p className="text-xs text-gray-500 mt-2">
                      Superseded by <span className="font-mono">{replacement.slug}</span>
                    </p>
                  )}
                  <p className="text-xs text-gray-400 mt-2">
                    Updated {formatWhen(w.updated_at)} via {w.created_via}
                  </p>
                </div>
              );
            })}
            {filteredWorkflows.length === 0 && !loading && (
              <p className="text-sm text-gray-500 text-center py-8">No workflows match this filter.</p>
            )}
          </div>
        </div>
      )}

      {tab === 'history' && (
        <div className="space-y-6">
          <section>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Directive revisions
            </h2>
            <div className="space-y-2">
              {revisions.map((r) => {
                const dir = directiveById.get(r.directive_id);
                return (
                  <div
                    key={r.id}
                    className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 px-4 py-3"
                  >
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-1">
                      <span className="font-medium text-gray-700 dark:text-gray-300">{r.event}</span>
                      <span>{formatWhen(r.created_at)}</span>
                      <span>via {r.source}</span>
                      {dir && (
                        <span className="font-mono text-gray-400">
                          {dir.key} ({dir.scope})
                        </span>
                      )}
                    </div>
                    {r.previous_text && (
                      <p className="text-xs text-gray-400 line-through">{r.previous_text}</p>
                    )}
                    {r.new_text && (
                      <p className="text-sm text-gray-800 dark:text-gray-200 mt-1">{r.new_text}</p>
                    )}
                    {r.note && <p className="text-xs text-amber-600 mt-1">Note: {r.note}</p>}
                  </div>
                );
              })}
              {revisions.length === 0 && !loading && (
                <p className="text-sm text-gray-500 text-center py-4">No directive revisions yet.</p>
              )}
            </div>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
              Workflow revisions
            </h2>
            <div className="space-y-2">
              {workflowRevisions.map((r) => {
                const wf = workflowById.get(r.workflow_id);
                return (
                  <div
                    key={r.id}
                    className="bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 px-4 py-3"
                  >
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 mb-1">
                      <span className="font-medium text-gray-700 dark:text-gray-300">{r.event}</span>
                      <span>{formatWhen(r.created_at)}</span>
                      <span>via {r.source}</span>
                      {wf && <span className="font-mono text-gray-400">{wf.slug}</span>}
                    </div>
                    {r.previous_status && r.new_status && (
                      <p className="text-xs text-gray-500">
                        {r.previous_status} → {r.new_status}
                      </p>
                    )}
                    {r.note && <p className="text-xs text-amber-600 mt-1">Note: {r.note}</p>}
                  </div>
                );
              })}
              {workflowRevisions.length === 0 && !loading && (
                <p className="text-sm text-gray-500 text-center py-4">No workflow revisions yet.</p>
              )}
            </div>
          </section>
        </div>
      )}

      {showDirectiveModal && (
        <DirectiveModal
          directive={editingDirective}
          onClose={() => setShowDirectiveModal(false)}
          onSaved={fetchAll}
        />
      )}
      {showBriefModal && (
        <BriefModal
          brief={editingBrief}
          onClose={() => setShowBriefModal(false)}
          onSaved={fetchAll}
        />
      )}
      {showWorkflowModal && (
        <WorkflowModal
          workflow={editingWorkflow}
          onClose={() => setShowWorkflowModal(false)}
          onSaved={fetchAll}
        />
      )}
    </div>
  );
};

export default AgentDirectivesView;
